import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  Flame, Newspaper, Sparkles, Clock, Eye, Heart, MessageSquare, 
  Search, Filter, Plus, ChevronRight, Share2, Tag, ArrowLeft, 
  ExternalLink, CheckCircle2, Shield, Calendar, User, Zap, X
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";
import { renderMarkdown } from "../components/ReplyEditor";
import { Avatar, Username, VipBadge } from "../components/UserBadge";

const CATEGORIES = [
  { id: "all", label: "Toate Știrile", icon: GlobeIcon, color: "#ff4f00" },
  { id: "cs2_updates", label: "Valve & CS2 Updates", icon: Zap, color: "#00F0FF" },
  { id: "esports", label: "Esports & Turnee", icon: Flame, color: "#FFB800" },
  { id: "community", label: "Comunitate & Servere", icon: Shield, color: "#00E676" },
  { id: "tech", label: "Hardware & Optimizare", icon: Sparkles, color: "#A200FF" },
];

function GlobeIcon(props) {
  return <Newspaper {...props} />;
}

export default function News() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") || "all";
  const activeArticleSlug = searchParams.get("read");

  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  // Reader Modal State
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [syncingSteam, setSyncingSteam] = useState(false);

  // Admin Modal State (Create & Edit)
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [formTitle, setFormTitle] = useState("");
  const [formCategory, setFormCategory] = useState("cs2_updates");
  const [formSummary, setFormSummary] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formCover, setFormCover] = useState("");
  const [formTags, setFormTags] = useState("");
  const [formSource, setFormSource] = useState("");
  const [formSourceUrl, setFormSourceUrl] = useState("");
  const [formFeatured, setFormFeatured] = useState(false);
  const [savingArticle, setSavingArticle] = useState(false);

  const isStaff = user && (user.role === "root" || user.role === "admin");

  useEffect(() => {
    fetchFeatured();
    fetchArticles();
  }, [activeCat, page]);

  useEffect(() => {
    if (activeArticleSlug) {
      openArticle(activeArticleSlug);
    } else {
      setSelectedArticle(null);
    }
  }, [activeArticleSlug]);

  const fetchFeatured = async () => {
    try {
      const res = await api.get("/news/featured");
      setFeatured(res.data.hero);
    } catch (e) {
      console.error("Error fetching featured news:", e);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 9 };
      if (activeCat !== "all") params.category = activeCat;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const res = await api.get("/news", { params });
      setArticles(res.data.articles || []);
      setTotalCount(res.data.total || 0);
    } catch (e) {
      console.error("Error fetching articles:", e);
      toast.error("Nu s-au putut încărca știrile.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchArticles();
  };

  const handleSyncSteam = async () => {
    setSyncingSteam(true);
    try {
      const res = await api.post("/news/sync-steam");
      toast.success(res.data.message || "Sincronizare Valve CS2 completă!");
      fetchFeatured();
      fetchArticles();
    } catch (e) {
      toast.error("Eroare la sincronizarea știrilor Valve.");
    } finally {
      setSyncingSteam(false);
    }
  };

  const openArticle = async (slugOrId) => {
    setArticleLoading(true);
    try {
      const res = await api.get(`/news/${slugOrId}`);
      const art = res.data.article;
      setSelectedArticle(art);
      setLikesCount(art.likes ? art.likes.length : 0);
      setLiked(user && art.likes ? art.likes.includes(user.id) : false);

      // fetch comments
      const commRes = await api.get(`/news/${art.id}/comments`);
      setComments(commRes.data.comments || []);
    } catch (e) {
      console.error("Error fetching article details:", e);
      toast.error("Articolul nu a putut fi găsit.");
      setSearchParams({});
    } finally {
      setArticleLoading(false);
    }
  };

  const handleEditClick = (art) => {
    setEditingArticleId(art.id);
    setFormTitle(art.title || "");
    setFormCategory(art.category || "cs2_updates");
    setFormSummary(art.summary || "");
    setFormContent(art.content || "");
    setFormCover(art.cover_image || "");
    setFormTags((art.tags || []).join(", "));
    setFormSource(art.source_name || "");
    setFormSourceUrl(art.source_url || "");
    setFormFeatured(art.is_featured || false);
    setShowAdminModal(true);
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Sigur doriți să ștergeți acest articol?")) return;
    try {
      await api.delete(`/news/${id}`);
      toast.success("Articolul a fost șters.");
      setSelectedArticle(null);
      setSearchParams({});
      fetchFeatured();
      fetchArticles();
    } catch (e) {
      toast.error("Eroare la ștergerea articolului.");
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a aprecia un articol.");
      return;
    }
    if (!selectedArticle) return;
    try {
      const res = await api.post(`/news/${selectedArticle.id}/like`);
      setLiked(res.data.user_liked);
      setLikesCount(res.data.likes_count);
    } catch (e) {
      toast.error("Eroare la aprecierea articolului.");
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a comenta.");
      return;
    }
    if (!commentText.trim()) return;

    setCommentSubmitting(true);
    try {
      const res = await api.post(`/news/${selectedArticle.id}/comments`, { content: commentText });
      setComments([...comments, res.data]);
      setCommentText("");
      toast.success("Comentariul tău a fost adăugat!");
    } catch (e) {
      toast.error("Eroare la postarea comentariului.");
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleSaveArticle = async (e) => {
    e.preventDefault();
    if (!formTitle.trim() || !formContent.trim()) {
      toast.error("Titlul și conținutul sunt obligatorii.");
      return;
    }

    setSavingArticle(true);
    try {
      const payload = {
        title: formTitle.trim(),
        summary: formSummary.trim() || formTitle.trim(),
        content: formContent.trim(),
        category: formCategory,
        tags: formTags.split(",").map(t => t.trim()).filter(Boolean),
        cover_image: formCover.trim() || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1200&q=80",
        is_featured: formFeatured,
        source_name: formSource.trim(),
        source_url: formSourceUrl.trim()
      };

      if (editingArticleId) {
        await api.patch(`/news/${editingArticleId}`, payload);
        toast.success("Știrea a fost actualizată!");
      } else {
        await api.post("/news", payload);
        toast.success("Știrea a fost publicată cu succes!");
      }

      setShowAdminModal(false);
      setEditingArticleId(null);
      // Reset form
      setFormTitle("");
      setFormSummary("");
      setFormContent("");
      setFormCover("");
      setFormTags("");
      fetchFeatured();
      fetchArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la salvarea știrii.");
    } finally {
      setSavingArticle(false);
    }
  };

  const getCategoryMeta = (catId) => {
    return CATEGORIES.find(c => c.id === catId) || { label: "General", color: "#ff4f00", icon: Newspaper };
  };

  return (
    <div className="min-h-screen bg-[#0b0e14] text-white py-6 px-4 md:px-8">
      {/* Background ambient lighting */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-[var(--tf-primary)]/10 rounded-full blur-[140px]" />
        <div className="absolute top-1/3 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto space-y-8">
        
        {/* HEADER & CONTROLS */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono text-[var(--tf-primary)] tracking-wider uppercase mb-1">
              <Sparkles className="w-3.5 h-3.5" />
              <span>INDUNGI GAMING NEWS & PATCH NOTES HUB</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-black tracking-tight text-white flex items-center gap-3">
              <Newspaper className="w-8 h-8 text-[var(--tf-primary)]" />
              ȘTIRI & ACTUALIZĂRI
            </h1>
            <p className="text-xs md:text-sm text-zinc-400 mt-1 max-w-2xl">
              Cele mai noi update-uri oficiale Valve, patch notes CS2, noutăți despre serverele comunității și rezumate generate automat de **INDUNGI @BOT**.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {isStaff && (
              <>
                <button
                  onClick={handleSyncSteam}
                  disabled={syncingSteam}
                  className="px-4 py-2.5 rounded-lg text-xs font-bold tracking-wider uppercase flex items-center gap-2 bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 transition-all shadow-lg disabled:opacity-50"
                >
                  <Zap className={`w-4 h-4 ${syncingSteam ? "animate-spin" : ""}`} />
                  {syncingSteam ? "Se sincronizează..." : "Sincronizează Valve (Steam)"}
                </button>
                <button
                  onClick={() => {
                    setEditingArticleId(null);
                    setFormTitle("");
                    setFormCategory("cs2_updates");
                    setFormSummary("");
                    setFormContent("");
                    setFormCover("");
                    setFormTags("");
                    setFormSource("");
                    setFormSourceUrl("");
                    setFormFeatured(false);
                    setShowAdminModal(true);
                  }}
                  className="tf-btn-primary px-4 py-2.5 rounded-lg text-xs font-black tracking-wider uppercase flex items-center gap-2 bg-[var(--tf-primary)] text-black hover:opacity-90 transition-all shadow-lg shadow-[var(--tf-primary)]/20"
                >
                  <Plus className="w-4 h-4" />
                  Adaugă Știre
                </button>
              </>
            )}
          </div>
        </div>

        {/* FEATURED HERO ARTICLE */}
        {featured && activeCat === "all" && !searchQuery && (
          <div className="relative rounded-2xl overflow-hidden border border-white/15 bg-gradient-to-t from-black via-zinc-950/80 to-zinc-900/60 shadow-2xl group">
            <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[380px]">
              
              {/* Image banner side */}
              <div className="lg:col-span-7 relative h-64 lg:h-full overflow-hidden">
                <img
                  src={featured.cover_image || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1200&q=80"}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 brightness-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/90 via-black/40 to-transparent" />
                
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-[var(--tf-primary)] text-black flex items-center gap-1.5 shadow-lg">
                    <Flame className="w-3.5 h-3.5 fill-black" />
                    FEATURED STORY
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-black/70 backdrop-blur-md border border-white/10 text-cyan-400">
                    {getCategoryMeta(featured.category).label}
                  </span>
                </div>
              </div>

              {/* Text content side */}
              <div className="lg:col-span-5 p-6 md:p-8 flex flex-col justify-between space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-xs text-zinc-400 font-medium">
                    <span className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
                      {featured.author_name}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                      {new Date(featured.created_at).toLocaleDateString("ro-RO")}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5 text-zinc-500" />
                      {featured.views || 0} vizualizări
                    </span>
                  </div>

                  <h2 className="text-xl md:text-2xl font-black text-white leading-tight group-hover:text-[var(--tf-primary)] transition-colors">
                    {featured.title}
                  </h2>

                  <p className="text-xs md:text-sm text-zinc-300 line-clamp-3 leading-relaxed">
                    {featured.summary}
                  </p>

                  {/* AI TL;DR Badge Box */}
                  <div className="bg-[var(--tf-primary)]/10 border border-[var(--tf-primary)]/20 rounded-lg p-3 text-xs text-zinc-300 space-y-1">
                    <div className="flex items-center gap-1.5 font-bold text-[var(--tf-primary)]">
                      <Zap className="w-3.5 h-3.5" />
                      Sinteză Rapidă AI:
                    </div>
                    <p className="text-[11px] text-zinc-300 line-clamp-2">
                      Patch major axat pe sub-tick responsiveness, fix-uri pe Dust2/Inferno și optimizări VRAM de performanță.
                    </p>
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-between">
                  <button
                    onClick={() => setSearchParams({ read: featured.slug || featured.id })}
                    className="tf-btn-primary px-5 py-2.5 rounded-lg text-xs font-black tracking-wider uppercase flex items-center gap-2 bg-[var(--tf-primary)] text-black hover:opacity-90 transition-all"
                  >
                    Citește Articolul Complet
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {featured.source_url && (
                    <a
                      href={featured.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                    >
                      <span>Sursă</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* CATEGORY FILTER TABS & SEARCH BAR */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-black/40 border border-white/10 rounded-xl p-3 backdrop-blur-md">
          {/* Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
            {CATEGORIES.map(c => {
              const Icon = c.icon;
              const isActive = activeCat === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => {
                    setSearchParams(c.id === "all" ? {} : { cat: c.id });
                    setPage(1);
                  }}
                  className={`px-3.5 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all flex items-center gap-2 border ${
                    isActive
                      ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] font-black shadow-md shadow-[var(--tf-primary)]/20"
                      : "bg-white/5 text-zinc-400 border-white/5 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isActive ? "text-black" : "text-zinc-400"}`} />
                  <span>{c.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="relative min-w-[240px]">
            <input
              type="text"
              placeholder="Caută în știri & actualizări..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black/60 border border-white/15 rounded-lg pl-9 pr-4 py-2 text-xs text-white placeholder-zinc-500 outline-none focus:border-[var(--tf-primary)] transition-all"
            />
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-2.5" />
          </form>
        </div>

        {/* ARTICLES GRID */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(n => (
              <div key={n} className="h-80 rounded-xl bg-white/5 animate-pulse border border-white/5" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16 bg-black/30 rounded-2xl border border-white/10 space-y-3">
            <Newspaper className="w-12 h-12 text-zinc-600 mx-auto" />
            <h3 className="text-lg font-bold text-white">Nu am găsit nicio știre în această categorie</h3>
            <p className="text-xs text-zinc-400">Încearcă să selectezi altă categorie sau să modifici căutarea.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map(art => {
              const catMeta = getCategoryMeta(art.category);
              return (
                <div
                  key={art.id}
                  onClick={() => setSearchParams({ read: art.slug || art.id })}
                  className="rounded-xl overflow-hidden border border-white/10 bg-zinc-950/60 hover:border-[var(--tf-primary)]/50 transition-all duration-300 flex flex-col justify-between group cursor-pointer shadow-lg hover:shadow-xl hover:shadow-[var(--tf-primary)]/5"
                >
                  <div>
                    {/* Cover image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={art.cover_image || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1200&q=80"}
                        alt={art.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 brightness-90"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-black/80 backdrop-blur-md border border-white/15 text-white">
                        {catMeta.label}
                      </span>
                    </div>

                    {/* Body */}
                    <div className="p-5 space-y-2.5">
                      <div className="flex items-center gap-2 text-[11px] text-zinc-400">
                        <span>{art.author_name || "INDUNGI"}</span>
                        <span>•</span>
                        <span>{new Date(art.created_at).toLocaleDateString("ro-RO")}</span>
                      </div>

                      <h3 className="text-base font-bold text-white leading-snug group-hover:text-[var(--tf-primary)] transition-colors line-clamp-2">
                        {art.title}
                      </h3>

                      <p className="text-xs text-zinc-400 line-clamp-2 leading-relaxed">
                        {art.summary}
                      </p>
                    </div>
                  </div>

                  {/* Footer Meta */}
                  <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" />
                        {art.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3.5 h-3.5 text-red-400" />
                        {art.likes ? art.likes.length : 0}
                      </span>
                    </div>

                    <span className="text-[var(--tf-primary)] font-bold flex items-center gap-1 text-[11px] uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                      Citește
                      <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {/* ARTICLE READER MODAL / OVERLAY */}
      {selectedArticle && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-2 md:p-6 overflow-y-auto">
          <div className="relative w-full max-w-4xl bg-zinc-950 border border-white/20 rounded-2xl overflow-hidden shadow-2xl my-auto max-h-[92vh] flex flex-col">
            
            {/* Header / Top Bar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-black/60 sticky top-0 z-20 backdrop-blur-md">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSearchParams({})}
                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
                  {getCategoryMeta(selectedArticle.category).label}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {isStaff && (
                  <>
                    <button
                      onClick={() => handleEditClick(selectedArticle)}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30"
                    >
                      Editează
                    </button>
                    <button
                      onClick={() => handleDeleteArticle(selectedArticle.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30"
                    >
                      Șterge
                    </button>
                  </>
                )}

                <button
                  onClick={handleLike}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 border transition-all ${
                    liked 
                      ? "bg-red-500/20 border-red-500/40 text-red-400 shadow-md shadow-red-500/20" 
                      : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"
                  }`}
                >
                  <Heart className={`w-3.5 h-3.5 ${liked ? "fill-red-400" : ""}`} />
                  <span>{likesCount} Aprecieri</span>
                </button>

                <button
                  onClick={() => setSearchParams({})}
                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Article Body */}
            <div className="overflow-y-auto p-6 md:p-8 space-y-6">
              
              {/* Cover Banner */}
              {selectedArticle.cover_image && (
                <div className="relative h-64 md:h-80 rounded-xl overflow-hidden border border-white/10 shadow-lg">
                  <img
                    src={selectedArticle.cover_image}
                    alt={selectedArticle.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                </div>
              )}

              {/* Title & Metadata */}
              <div className="space-y-3">
                <h1 className="text-2xl md:text-3xl font-black text-white leading-tight">
                  {selectedArticle.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-400 pt-1 pb-3 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <img
                      src={selectedArticle.author_avatar || "https://api.dicebear.com/7.x/bottts/svg?seed=IndungiBot"}
                      alt={selectedArticle.author_name}
                      className="w-6 h-6 rounded-full border border-white/20"
                    />
                    <span className="font-bold text-white">{selectedArticle.author_name}</span>
                  </div>
                  <span>•</span>
                  <span>{new Date(selectedArticle.created_at).toLocaleDateString("ro-RO", { year: "numeric", month: "long", day: "numeric" })}</span>
                  <span>•</span>
                  <span>{selectedArticle.views || 0} vizualizări</span>
                  
                  {selectedArticle.source_url && (
                    <>
                      <span>•</span>
                      <a
                        href={selectedArticle.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--tf-primary)] hover:underline flex items-center gap-1 font-bold"
                      >
                        <span>Sursă: {selectedArticle.source_name || "Official"}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </>
                  )}
                </div>
              </div>

              {/* AI Quick Summary TL;DR Box */}
              <div className="bg-[var(--tf-primary)]/10 border border-[var(--tf-primary)]/30 rounded-xl p-4 text-xs text-zinc-200 space-y-1.5">
                <div className="flex items-center gap-2 font-black text-[var(--tf-primary)] uppercase tracking-wider text-[11px]">
                  <Zap className="w-4 h-4" />
                  💡 Sinteză AI realizată de INDUNGI @BOT:
                </div>
                <p className="leading-relaxed">
                  {selectedArticle.summary}
                </p>
              </div>

              {/* Main Content Markdown */}
              <div 
                className="prose prose-invert max-w-none text-zinc-200 text-sm md:text-base leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedArticle.content) }}
              />

              {/* Tags */}
              {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-white/10">
                  <Tag className="w-3.5 h-3.5 text-zinc-500" />
                  {selectedArticle.tags.map((t, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-white/5 border border-white/10 text-zinc-300"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
              )}

              {/* Comments Section */}
              <div className="pt-8 border-t border-white/10 space-y-6">
                <h3 className="text-lg font-black text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[var(--tf-primary)]" />
                  Comentarii ({comments.length})
                </h3>

                {/* Add Comment Box */}
                {user ? (
                  <form onSubmit={handleAddComment} className="space-y-3 bg-black/40 border border-white/10 rounded-xl p-4">
                    <textarea
                      placeholder="Scrie un comentariu la această știre..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={3}
                      className="w-full bg-black/60 border border-white/15 rounded-lg p-3 text-xs text-white placeholder-zinc-500 outline-none focus:border-[var(--tf-primary)] resize-none"
                    />
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={commentSubmitting || !commentText.trim()}
                        className="tf-btn-primary px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-[var(--tf-primary)] text-black hover:opacity-90 disabled:opacity-50"
                      >
                        {commentSubmitting ? "Se postează..." : "Postează Comentariu"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center text-xs text-zinc-400">
                    Trebuie să fii autentificat pentru a comenta. <Link to="/login" className="text-[var(--tf-primary)] font-bold hover:underline">Autentificare</Link>
                  </div>
                )}

                {/* Comments List */}
                <div className="space-y-3">
                  {comments.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic">Fii primul care comentează acest articol!</p>
                  ) : (
                    comments.map(c => (
                      <div key={c.id} className="bg-black/30 border border-white/5 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img
                              src={c.user_avatar || "https://api.dicebear.com/7.x/bottts/svg?seed=user"}
                              alt={c.username}
                              className="w-6 h-6 rounded-full border border-white/15"
                            />
                            <span className="text-xs font-bold text-white">{c.username}</span>
                            {c.user_role === "root" && <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-red-500/20 text-red-400 border border-red-500/30">ROOT</span>}
                            {c.user_role === "admin" && <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-amber-500/20 text-amber-400 border border-amber-500/30">ADMIN</span>}
                          </div>
                          <span className="text-[10px] text-zinc-500">{new Date(c.created_at).toLocaleDateString("ro-RO")}</span>
                        </div>
                        <p className="text-xs text-zinc-300 pl-8 leading-relaxed">{c.content}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

            </div>

          </div>
        </div>
      )}

      {/* ADMIN CREATE ARTICLE MODAL */}
      {showAdminModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-zinc-950 border border-white/20 rounded-2xl p-6 shadow-2xl space-y-5 my-auto max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-lg font-black text-white flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-[var(--tf-primary)]" />
                Publică o Știre Nouă
              </h2>
              <button onClick={() => setShowAdminModal(false)} className="p-1 rounded bg-white/5 hover:bg-white/10 text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateArticle} className="space-y-4 text-xs">
              <div>
                <label className="block text-zinc-400 font-bold mb-1 uppercase">Titlu Articol *</label>
                <input
                  type="text"
                  required
                  placeholder="ex: Valve a lansat noul patch CS2..."
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1 uppercase">Categorie</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  >
                    <option value="cs2_updates">Valve & CS2 Updates</option>
                    <option value="esports">Esports & Turnee</option>
                    <option value="community">Comunitate & Servere</option>
                    <option value="tech">Hardware & Optimizare</option>
                  </select>
                </div>

                <div>
                  <label className="block text-zinc-400 font-bold mb-1 uppercase">Imagine Copertă URL</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={formCover}
                    onChange={(e) => setFormCover(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1 uppercase">Rezumat / Sinteză Rapidă (TL;DR) *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="Scurt rezumat în 2-3 propoziții care va apărea pe card și în caseta AI..."
                  value={formSummary}
                  onChange={(e) => setFormSummary(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)] resize-none"
                />
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1 uppercase">Conținut Articol (suportă Markdown) *</label>
                <textarea
                  required
                  rows={8}
                  placeholder="Scrie textul complet al articolului... Poți folosi subtitluri ##, liste, etc."
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)] font-mono text-xs resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1 uppercase">Tag-uri (separate prin virgulă)</label>
                  <input
                    type="text"
                    placeholder="CS2, Valve, Update, Patch"
                    value={formTags}
                    onChange={(e) => setFormTags(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-400 font-bold mb-1 uppercase">Nume Sursă & Link</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="ex: Valve Blog"
                      value={formSource}
                      onChange={(e) => setFormSource(e.target.value)}
                      className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                    />
                    <input
                      type="url"
                      placeholder="https://..."
                      value={formSourceUrl}
                      onChange={(e) => setFormSourceUrl(e.target.value)}
                      className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="featuredCheck"
                  checked={formFeatured}
                  onChange={(e) => setFormFeatured(e.target.checked)}
                  className="w-4 h-4 accent-[var(--tf-primary)]"
                />
                <label htmlFor="featuredCheck" className="text-zinc-300 font-medium cursor-pointer">
                  Marchează ca Articol Principal (Hero Featured)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowAdminModal(false)}
                  className="px-4 py-2 rounded-lg bg-white/10 text-white font-bold hover:bg-white/20"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  disabled={savingArticle}
                  className="tf-btn-primary px-5 py-2 rounded-lg bg-[var(--tf-primary)] text-black font-black uppercase tracking-wider hover:opacity-90 disabled:opacity-50"
                >
                  {savingArticle ? "Se publică..." : "Publică Știrea"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
